import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import { Row, Col, Button, TabContent, TabPane, Card, CardBody, Alert } from 'reactstrap';

import { useScheduling } from '../../../hooks/useScheduling';
import { useVendorExecutionStatus } from '../../../hooks/useVendorExecutionStatus';
import BZAPIService from '../../../services/BZAPIService';

import WizardTabNav from './WizardTabNav';
import TabOne from '../section/TabOne';
import RiskScore from '../section/RiskScore';
import HistoryTab from '../section/HistoryTab';
import ManagedProductsTab from '../section/ManagedProductsTab';
import AITab from '../section/AITab';
import SummaryModal from '../Vendor/Modal';

import WizardAccordion from './WizardAccordion';
import DynamicCredentialsContent from './DynamicCredentialsContent';
import DynamicDefaultConfigExtras from './DynamicDefaultConfigExtras';
import ScheduleAccordionContent from './ScheduleAccordionContent';
import ServiceNowSettingsContent from './ServiceNowSettingsContent';
import DynamicFilterConfig from './DynamicFilterConfig';
import { useDynamicValidation } from './useDynamicValidation';
import { useDynamicSavePayload } from './useDynamicSavePayload';
import { buildInitialDynamicState } from './schemaTransforms';
import { evaluateSecretCondition, buildSecretPayload } from './DynamicCredentialsTab';

/**
 * Dynamic configuration form that renders vendor config from a JSON schema.
 * Uses a wizard-style accordion layout with 4 tabs:
 *   1. Overview (read-only dashboard)
 *   2. Configuration (wizard accordion: Credentials → Settings → Schedule → Priority/Filters → AI)
 *   3. Risk Score (optional)
 *   4. History
 */
const DynamicConfigForm = ({
  formSchema,
  settings,
  vendor,
  isReadOnly,
  vendorLabel,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  vendorPriorityOptions,
  vendorStatusOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  isCve,
  setIsCve,
  bugRisk,
  setBugRisk,
  loading,
  excludedComponents,
  setExcludedComponents,
  isServiceActive,
  setIsServiceActive,
  allRequired: parentAllRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated,
  autoClose,
  setAutoClose,
  enableChatGptPriority,
  onAiEnabledChange,
  aiEnabled,
  aiRequired,
  vendorAiEnabled,
  aiToggleNeedsResave,
  setAiToggleNeedsResave,
  ...restProps
}) => {
  const navigate = useNavigate();

  // Local UI state
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);
  const [riskError, setRiskError] = useState('');
  const [isUpdatingCreds, setIsUpdatingCreds] = useState(false);
  const [isAddingCredentials] = useState(false);
  const [isValidCredentials, setIsValidCredentials] = useState(false);

  // Dynamic state for post-credential widgets and schema-driven fields
  const [dynamicState, setDynamicState] = useState({});

  // STRY0037097 — Cisco product series / family bug retrieval
  const [enableProductFamilyBugs, setEnableProductFamilyBugs] = useState(true);

  // STRY0037097 — ODD inferred-version bug matching (vmware vc/esx/nsx, fortinet, paloalto)
  const [enableInferredVersions, setEnableInferredVersions] = useState(true);

  // STRY0036842 — Red Hat z-stream bug retrieval (opt-in, default off; only rh.json declares the toggle)
  const [enableVersionStreamBugs, setEnableVersionStreamBugs] = useState(false);

  // Component loading state (for vendors with component filters)
  const [includedComponents, setIncludedComponents] = useState([]);
  const [loader, setLoader] = useState(false);
  const [query, setQuery] = useState('');
  const [offset] = useState('0');
  const [limit] = useState('2500');

  // Credential tab ref for saving secrets
  const credTabRef = useRef(null);

  // Wizard accordion state
  const [openSections, setOpenSections] = useState(['creds']);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [cachedManagedProducts, setCachedManagedProducts] = useState(null);

  const toggleSection = id => {
    setOpenSections(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  // Scheduling hook
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    saveBugServiceFrequency
  } = useScheduling();

  // Vendor execution-status hook lives at this level (not inside TabOne) so
  // handleSave below can call refresh() right after a save lands — the
  // status card flips from idle to running without waiting on the next
  // polling tick.
  const {
    status: execStatus,
    loading: execStatusLoading,
    refresh: refreshExecStatus
  } = useVendorExecutionStatus(vendorId);

  // Reset cached data and load schedule when vendor changes
  useEffect(() => {
    setCachedManagedProducts(null);
    setScheduleLoaded(false);

    const isCloud = vendor?.cloudVendor === 1;
    const defaultUnit = isCloud ? 'minute' : 'hour';
    const defaultValue = isCloud ? { label: '30', value: '30' } : { label: '4', value: 4 };

    const extractDaysFromCron = awsCron => {
      const parts = awsCron
        .replace('cron(', '')
        .replace(')', '')
        .split(' ');
      if (parts.length >= 5) {
        const dayPart = parts[4];
        if (dayPart === '*') return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
        if (dayPart === '?') return [];
        return dayPart.split(',').map(d => {
          if (d.length === 3) return d.toUpperCase();
          const dayMap = { 1: 'SUN', 2: 'MON', 3: 'TUE', 4: 'WED', 5: 'THU', 6: 'FRI', 7: 'SAT' };
          return dayMap[d] || d.toUpperCase();
        });
      }
      return [];
    };

    const extractTimesFromCron = awsCron => {
      const parts = awsCron
        .replace('cron(', '')
        .replace(')', '')
        .split(' ');
      if (parts.length >= 2) {
        const minutePart = parts[0];
        const hourPart = parts[1];
        if (hourPart.includes('*/')) {
          const interval = parseInt(hourPart.split('*/')[1]);
          const times = [];
          for (let h = 0; h < 24; h += interval) {
            const m = minutePart === '*' ? 0 : parseInt(minutePart);
            times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
          }
          return times;
        }
        if (hourPart.includes(',')) {
          return hourPart.split(',').map(hour => {
            const h = parseInt(hour);
            const m = minutePart === '*' ? 0 : parseInt(minutePart);
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
          });
        }
        if (hourPart !== '*' && !hourPart.includes('/')) {
          const h = parseInt(hourPart);
          const m = minutePart === '*' ? 0 : parseInt(minutePart);
          return [`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`];
        }
      }
      return ['06:00'];
    };

    BZAPIService.getBugServiceSchedule(vendorId)
      .then(res => {
        const data = res?.data;
        if (data?.mode && data.rules && data.rules.length > 0) {
          const rule = data.rules[0];
          setSchedulingMode(data.mode);
          if (data.mode === 'interval') {
            let unit, number;
            if (rule.awsCron) {
              const cronParts = rule.awsCron
                .replace('cron(', '')
                .replace(')', '')
                .split(' ');
              const minutePart = cronParts[0];
              const hourPart = cronParts[1];
              if (hourPart.includes('*/')) {
                unit = 'hour';
                number = parseInt(hourPart.split('*/')[1]);
              } else if (hourPart === '*' && !minutePart.includes('*') && !minutePart.includes(',')) {
                unit = 'hour';
                number = 1;
              } else if (minutePart.includes('*/')) {
                unit = 'minute';
                number = parseInt(minutePart.split('*/')[1]);
              }
            }
            if (!unit && rule.schedule) {
              unit = rule.schedule.timeUnit || rule.schedule.unit;
              number = rule.schedule.number || rule.schedule.every;
              if (unit === 'hours') unit = 'hour';
              if (unit === 'minutes') unit = 'minute';
            }
            if (!unit && data.timeUnit) {
              unit = data.timeUnit;
              number = data.number;
            }
            if (!unit) unit = defaultUnit;
            setTimeUnit(unit);
            setTimeValue(number ? { label: number, value: number } : defaultValue);
          } else if (data.mode === 'calendar') {
            setSchedules(
              data.rules.map(r => ({
                days: extractDaysFromCron(r.awsCron),
                times: extractTimesFromCron(r.awsCron),
                nextRunUTC: r.nextRunUTC,
                name: r.name,
                awsCron: r.awsCron
              }))
            );
          }
        } else if (data?.timeUnit || data?.number) {
          setSchedulingMode('interval');
          setTimeUnit(data.timeUnit || defaultUnit);
          const val = data.number;
          setTimeValue(val ? { label: val, value: val } : defaultValue);
        } else {
          setSchedulingMode('interval');
          setTimeUnit(defaultUnit);
          setTimeValue(defaultValue);
        }
        setScheduleLoaded(true);
      })
      .catch(() => setScheduleLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // Warn the user before unloading (refresh/close) when AI toggle has unsaved priority mappings
  useEffect(() => {
    if (!aiToggleNeedsResave) return undefined;
    const handler = e => {
      e.preventDefault();
      e.returnValue = ''; // required for legacy browsers
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [aiToggleNeedsResave]);

  // Collect all props for validation and payload hooks
  const allProps = useMemo(
    () => ({
      bugsDateBack,
      vendorStatuses,
      priorityMaps,
      snCompanySysId,
      fields,
      vendorEventScopes: restProps.vendorEventScopes,
      vendorBugServices: restProps.vendorBugServices,
      vendorBugCategories: restProps.vendorBugCategories,
      vendorBugTypes: restProps.vendorBugTypes,
      vendorImpactedRegions: restProps.vendorImpactedRegions,
      vendorProductBugCategories: restProps.vendorProductBugCategories,
      keywords,
      searchFields,
      excludedComponents,
      isCve,
      bugRisk,
      enableProductFamilyBugs,
      enableInferredVersions,
      enableVersionStreamBugs,
      aiEnabled,
      aiRequired,
      vendorAiEnabled,
      autoClose,
      ...restProps
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bugsDateBack,
      vendorStatuses,
      priorityMaps,
      snCompanySysId,
      fields,
      keywords,
      searchFields,
      excludedComponents,
      isCve,
      bugRisk,
      enableProductFamilyBugs,
      enableInferredVersions,
      enableVersionStreamBugs,
      restProps,
      aiEnabled,
      aiRequired,
      vendorAiEnabled,
      autoClose
    ]
  );

  // Schema-driven validation
  const { allRequired, validateForSave } = useDynamicValidation(formSchema, allProps, dynamicState);

  // Schema-driven save payload
  const { buildPayload } = useDynamicSavePayload(formSchema, allProps, dynamicState);

  // Keep parent allRequired in sync
  useEffect(() => {
    setAllRequired(allRequired);
  }, [allRequired, setAllRequired]);

  // Mark form as updated on any state change
  useEffect(() => {
    setIsUpdated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    bugsDateBack,
    vendorStatuses,
    priorityMaps,
    keywords,
    searchFields,
    excludedComponents,
    isCve,
    snCompanySysId,
    bugRisk,
    dynamicState,
    enableProductFamilyBugs,
    enableInferredVersions,
    enableVersionStreamBugs
  ]);

  // Load components on mount if priority config requires it
  // Load components on mount if priority config requires it
  useEffect(() => {
    if (formSchema?.priorityConfig?.showComponents) {
      loadComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize dynamic state from settings when settings load
  // settingsKey maps the saved setting name to the widget's stateKey (e.g. "aggregatorName" → "aggregator")
  // Settings can be either {value: {...}} wrapper or flat object depending on API response
  useEffect(() => {
    if (!settings) return;
    const settingsValues = settings?.value || settings || {};
    const widgets = formSchema?.credentials?.postCredentialWidgets || [];
    const extras = formSchema?.defaultConfigExtras || [];
    // Excludes dedicated-useState toggle keys (enableVersionStreamBugs, autoClose,
    // etc.) — those are restored via their own setters below and flow through
    // allProps. Mirroring them into dynamicState let a stale loaded value win at
    // save time, so the toggle couldn't be disabled (GH #436).
    const initialDynamic = buildInitialDynamicState(widgets, extras, settingsValues);
    if (Object.keys(initialDynamic).length) {
      setDynamicState(prev => ({ ...prev, ...initialDynamic }));
    }
    if (settingsValues && Object.prototype.hasOwnProperty.call(settingsValues, 'enableProductFamilyBugs')) {
      setEnableProductFamilyBugs(!!settingsValues.enableProductFamilyBugs);
    }
    if (settingsValues) {
      if (Object.prototype.hasOwnProperty.call(settingsValues, 'enableInferredVersions')) {
        setEnableInferredVersions(!!settingsValues.enableInferredVersions);
      } else if (Object.prototype.hasOwnProperty.call(settingsValues, 'excludeInferredVersions')) {
        // legacy key — invert so the toggle reflects the user's intent
        setEnableInferredVersions(!settingsValues.excludeInferredVersions);
      }
    }
    if (settingsValues && Object.prototype.hasOwnProperty.call(settingsValues, 'enableVersionStreamBugs')) {
      setEnableVersionStreamBugs(!!settingsValues.enableVersionStreamBugs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  async function loadComponents() {
    setLoader(true);
    try {
      const components = (await BZAPIService.getComponents(vendorId, query, offset, limit)) || [];
      settings?.excludedBugComponents
        ? setExcludedComponents(settings.excludedBugComponents)
        : setExcludedComponents([]);
      settings?.searchFields
        ? setSearchFields(settings.searchFields)
        : setSearchFields(vendor?.vendorData?.searchFields || []);
      setIncludedComponents(components?.results || []);
      setLoader(false);
    } catch (e) {
      console.error('Failed to load components:', e);
      setLoader(false);
    }
  }

  /**
   * Guarded setActiveTab that confirms before switching if AI toggle has unsaved priority mappings.
   * Only used by WizardTabNav (external tab switches); internal redirects bypass this guard.
   */
  const guardedSetActiveTab = useCallback(
    tab => {
      if (
        aiToggleNeedsResave &&
        !window.confirm('AI mode was changed but priority mappings have not been saved. Switch tabs anyway?')
      ) {
        return;
      }
      setActiveTab(tab);
    },
    [aiToggleNeedsResave]
  );

  /**
   * Save handler - validates, saves secrets, builds payload, saves config.
   */
  const handleSave = useCallback(async () => {
    setRiskError('');
    try {
      setIsSubmitted(true);
      setIsLoading(true);

      // Validate using schema-driven validation
      const validation = validateForSave({
        isValidCredentials,
        bugRisk
      });

      if (!validation.valid) {
        if (validation.error) toast.error(validation.error);
        setValidated(false);
        // Redirect to the correct tab and open the relevant accordion section
        if (validation.redirectTab === '5') {
          setActiveTab('5');
          setRiskError(validation.error || '');
        } else if (validation.section) {
          setActiveTab('2');
          setOpenSections([validation.section]);
        }
        setIsLoading(false);
        return;
      }

      // Save credential secrets
      let secretResults = {};
      if (formSchema?.credentials?.secrets) {
        const secrets = formSchema.credentials.secrets;
        for (const secretDef of secrets) {
          const conditionMet = evaluateSecretCondition(secretDef.condition, fields);
          if (conditionMet) {
            const payload = buildSecretPayload(secretDef.secretPayload, fields);
            const result = await BZAPIService.createOrUpdateAWSSecret(payload);
            secretResults[secretDef.resultKey] = result;
            if (result?.Name) {
              fields[secretDef.resultKey] = result.Name;
            }
          } else if (fields[secretDef.resultKey]) {
            secretResults[secretDef.resultKey] = { Name: fields[secretDef.resultKey] };
          }
        }
      }

      // Build and save the config payload
      const data = buildPayload(secretResults);

      await BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun, isUpdatingCreds);

      // Save scheduling
      await saveBugServiceFrequency(vendorId);

      // Reset state
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      const wasForceRun = isForceRun;
      setIsForceRun(false);
      setIsUpdatingCreds(false);
      setActiveTab('1');
      toast.success('Settings Saved!');
      setAiToggleNeedsResave?.(false);

      // Refresh the execution-status card now so the user doesn't sit on
      // stale status after Save / Save & Run Now. For force-run, schedule
      // a second refresh ~2.5 s later because the new state-machine
      // execution takes a brief moment to appear in ListExecutions.
      refreshExecStatus();
      if (wasForceRun) {
        setTimeout(refreshExecStatus, 2500);
      }
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.error('Save error:', e);
    }
  }, [
    formSchema,
    fields,
    vendorId,
    syncOnly,
    isForceRun,
    isUpdatingCreds,
    isValidCredentials,
    bugRisk,
    validateForSave,
    buildPayload,
    saveBugServiceFrequency,
    refreshExecStatus,
    setAiToggleNeedsResave
  ]);

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tabs = useMemo(() => formSchema?.tabs || {}, [formSchema]);

  // Compute completion state for wizard sections
  // Uses the same validation logic as useDynamicValidation to stay consistent
  const requiredFields = useMemo(() => formSchema?.validation?.requiredFields || [], [formSchema]);

  const completionState = useMemo(() => {
    const state = {};

    // Credentials: stored creds exist OR just validated in this session
    if (tabs.credentials && formSchema?.credentials) {
      const storedCheckField = formSchema.credentials.storedCheckField;
      state.creds = !!(storedCheckField && fields[storedCheckField]) || isValidCredentials;
    }

    // ServiceNow: snCompanySysId must have value + priorityMaps must be valid
    state.servicenow =
      !!snCompanySysId?.value && !!priorityMaps?.length && !priorityMaps.some(m => !m.snPriority || !m.vendorPriority);

    // Schedule: interval needs unit + value, calendar needs at least one schedule
    if (schedulingMode === 'interval') {
      state.schedule = !!(timeUnit && timeValue?.value);
    } else {
      state.schedule = !!(schedules && schedules.length > 0);
    }

    // Filters: bugsDateBack required + all schema-required filter fields
    const filterFieldValues = {
      vendorStatuses,
      vendorBugServices: restProps.vendorBugServices,
      vendorBugCategories: restProps.vendorBugCategories,
      vendorBugTypes: restProps.vendorBugTypes,
      vendorEventScopes: restProps.vendorEventScopes,
      vendorImpactedRegions: restProps.vendorImpactedRegions,
      vendorProductBugCategories: restProps.vendorProductBugCategories
    };
    const filterFieldNames = Object.keys(filterFieldValues);
    const requiredFilterFields = requiredFields.filter(f => filterFieldNames.includes(f));
    state.filters = !!bugsDateBack?.length && requiredFilterFields.every(field => !!filterFieldValues[field]?.length);

    // AI: always considered complete (optional)
    if (vendor?.aiSupported || tabs.ai) {
      state.ai = true;
    }

    return state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tabs,
    formSchema,
    fields,
    bugsDateBack,
    snCompanySysId,
    schedulingMode,
    timeUnit,
    timeValue,
    schedules,
    priorityMaps,
    vendorStatuses,
    vendor,
    isValidCredentials,
    requiredFields,
    restProps.vendorBugServices,
    restProps.vendorBugCategories,
    restProps.vendorBugTypes,
    restProps.vendorEventScopes,
    restProps.vendorImpactedRegions,
    restProps.vendorProductBugCategories
  ]);

  // Build accordion sections
  const accordionSections = useMemo(() => {
    const sections = [];

    // 1. Vendor Credentials
    if (tabs.credentials && formSchema?.credentials) {
      const storedCheckField = formSchema.credentials.storedCheckField;
      const summary = fields[storedCheckField] ? 'Credentials stored' : undefined;
      sections.push({
        id: 'creds',
        title: 'Vendor Credentials',
        summary,
        content: (
          <DynamicCredentialsContent
            ref={credTabRef}
            schema={formSchema.credentials}
            vendorId={vendorId}
            fields={fields}
            handleFieldChange={handleFieldChange}
            isSubmitted={isSubmitted}
            dynamicState={dynamicState}
            setDynamicState={setDynamicState}
            onValidCredentials={setIsValidCredentials}
            {...restProps}
          />
        )
      });
    }

    // 2. ServiceNow Settings
    const snSummary = snCompanySysId?.value
      ? `${priorityMaps?.length || 0} mapping${priorityMaps?.length !== 1 ? 's' : ''}`
      : undefined;
    sections.push({
      id: 'servicenow',
      title: 'ServiceNow Settings',
      summary: snSummary,
      content: (
        <ServiceNowSettingsContent
          vendorId={vendorId}
          vendor={vendor}
          vendorLabel={vendorLabel}
          snCompanySysId={snCompanySysId}
          setSnCompanySysId={setSnCompanySysId}
          snCompanySysIdOptions={snCompanySysIdOptions}
          snCompanySysIdErrors={snCompanySysIdErrors}
          fields={fields}
          handleFieldChange={handleFieldChange}
          isSubmitted={isSubmitted}
          priorityMaps={priorityMaps}
          setPriorityMaps={setPriorityMaps}
          vendorPriorityOptions={vendorPriorityOptions}
          snPriorityOptions={snPriorityOptions}
          autoClose={autoClose}
          setAutoClose={setAutoClose}
          enableChatGptPriority={enableChatGptPriority}
        />
      )
    });

    // 3. Schedule
    const getScheduleSummary = () => {
      if (schedulingMode === 'interval' && timeUnit && timeValue?.value) {
        const unit = timeUnit === 'minute' ? 'min' : 'hr';
        return `Every ${timeValue.value} ${unit}${timeValue.value > 1 ? 's' : ''}`;
      }
      if (schedulingMode === 'calendar' && schedules?.length > 0) {
        return `${schedules.length} schedule${schedules.length > 1 ? 's' : ''}`;
      }
      return undefined;
    };
    sections.push({
      id: 'schedule',
      title: 'Schedule',
      summary: getScheduleSummary(),
      content: (
        <ScheduleAccordionContent
          vendorId={vendorId}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
        />
      )
    });

    // 4. Filter Settings
    const filterSummary = bugsDateBack ? `From ${bugsDateBack}` : undefined;
    sections.push({
      id: 'filters',
      title: 'Filter Settings',
      summary: filterSummary,
      content: (
        <DynamicFilterConfig
          schema={formSchema.priorityConfig || {}}
          requireImpactedRegions={requiredFields.includes('vendorImpactedRegions')}
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          isSubmitted={isSubmitted}
          bugsDateBack={bugsDateBack}
          setBugsDateBack={setBugsDateBack}
          isCve={isCve}
          setIsCve={setIsCve}
          vendorStatusOptions={vendorStatusOptions}
          vendorStatuses={vendorStatuses}
          setVendorStatuses={setVendorStatuses}
          keywords={keywords}
          setKeywords={setKeywords}
          searchFields={searchFields}
          setSearchFields={setSearchFields}
          includedComponents={includedComponents}
          setIncludedComponents={setIncludedComponents}
          excludedComponents={excludedComponents}
          setExcludedComponents={setExcludedComponents}
          loader={loader}
          setLoader={setLoader}
          setQuery={setQuery}
          query={query}
          offset={offset}
          limit={limit}
          vendorResolutionOptions={restProps.vendorResolutionOptions}
          vendorResolutions={restProps.vendorResolutions}
          setVendorResolutions={restProps.setVendorResolutions}
          vendorBugCategoryOptions={restProps.vendorBugCategoryOptions}
          vendorBugCategories={restProps.vendorBugCategories}
          setVendorBugCategories={restProps.setVendorBugCategories}
          vendorBugTypeOptions={restProps.vendorBugTypeOptions}
          vendorBugTypes={restProps.vendorBugTypes}
          setVendorBugTypes={restProps.setVendorBugTypes}
          vendorBugServiceOptions={restProps.vendorBugServiceOptions}
          vendorBugServices={restProps.vendorBugServices}
          setVendorBugServices={restProps.setVendorBugServices}
          vendorEventScopes={restProps.vendorEventScopes}
          setVendorEventScopes={restProps.setVendorEventScopes}
          vendorEventScopeOptions={restProps.vendorEventScopeOptions}
          vendorImpactedRegions={restProps.vendorImpactedRegions}
          setVendorImpactedRegions={restProps.setVendorImpactedRegions}
          vendorImpactedRegionOptions={
            // After validating creds, regionOptions holds the full list from the
            // test endpoint. On load of a configured product no creds test has run,
            // so fall back to vendorData options and finally to the saved selections
            // themselves (mapped to {label,value}) — otherwise saved regions render
            // with blank labels because there are no options to resolve them. #454
            // (note: an empty array is truthy, so length checks are required here).
            dynamicState.regionOptions?.length
              ? dynamicState.regionOptions
              : restProps.vendorImpactedRegionOptions?.length
              ? restProps.vendorImpactedRegionOptions
              : (restProps.vendorImpactedRegions || []).map(r =>
                  r && typeof r === 'object' ? r : { label: r, value: r }
                )
          }
          vendorSupportStatusOptions={restProps.vendorSupportStatusOptions}
          vendorSupportStatus={restProps.vendorSupportStatus}
          setVendorSupportStatus={restProps.setVendorSupportStatus}
          vendorComplianceStatus={restProps.vendorComplianceStatus}
          setVendorComplianceStatus={restProps.setVendorComplianceStatus}
          vendorComplianceStatusOptions={restProps.vendorComplianceStatusOptions}
          vendorRiskCategoryOptions={restProps.vendorRiskCategoryOptions}
          vendorRiskCategories={restProps.vendorRiskCategories}
          setVendorRiskCategories={restProps.setVendorRiskCategories}
          vendorMitigationTypes={restProps.vendorMitigationTypes}
          setVendorMitigationTypes={restProps.setVendorMitigationTypes}
          vendorMitigationTypeOptions={restProps.vendorMitigationTypeOptions}
          vendorProductBugCategories={restProps.vendorProductBugCategories}
          setVendorProductBugCategories={restProps.setVendorProductBugCategories}
          vendorProductBugCategoryOptions={restProps.vendorProductBugCategoryOptions}
        >
          <DynamicDefaultConfigExtras
            extras={formSchema.defaultConfigExtras}
            props={{
              ...dynamicState,
              vendor,
              vendorStatuses,
              setVendorStatuses,
              autoClose,
              setAutoClose,
              enableProductFamilyBugs,
              setEnableProductFamilyBugs,
              enableInferredVersions,
              setEnableInferredVersions,
              enableVersionStreamBugs,
              setEnableVersionStreamBugs,
              setDynamicState,
              isSubmitted
            }}
          />
        </DynamicFilterConfig>
      )
    });

    // 5. AI Settings (optional)
    if (vendor?.aiSupported || tabs.ai) {
      sections.push({
        id: 'ai',
        title: 'AI Settings',
        summary: 'Configured',
        content: (
          <AITab
            vendorId={vendorId}
            isReadOnly={isReadOnly}
            aiTogglesPriorityType={!!formSchema?.priorityConfig?.aiTogglesPriorityType}
            priorityMaps={priorityMaps}
            onAiEnabledChange={onAiEnabledChange}
          />
        )
      });
    }

    return sections;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tabs,
    formSchema,
    vendorId,
    vendorLabel,
    vendor,
    fields,
    handleFieldChange,
    isSubmitted,
    dynamicState,
    bugsDateBack,
    snCompanySysId,
    snCompanySysIdErrors,
    snCompanySysIdOptions,
    autoClose,
    vendorStatuses,
    timeUnit,
    timeValue,
    schedulingMode,
    schedules,
    isConfigured,
    modalOpen,
    priorityMaps,
    keywords,
    searchFields,
    excludedComponents,
    includedComponents,
    isCve,
    loader,
    query,
    isReadOnly,
    enableProductFamilyBugs,
    enableInferredVersions,
    enableVersionStreamBugs
  ]);

  if (loading)
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
          <span className="sr-only">Loading...</span>
        </div>
        <p className="text-muted mb-0">Loading vendor details...</p>
      </div>
    );

  return (
    <>
      <WizardTabNav
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={guardedSetActiveTab}
        hasBugRisk={!!tabs.riskScore}
        loading={loading}
      />

      {aiToggleNeedsResave && (
        <Alert color="warning" fade={false} className="mb-3">
          <strong>AI mode changed.</strong> Set a new priority mapping in ServiceNow Settings and Save to complete the
          change — the new mapping will be applied to every managed product on the next service run.
        </Alert>
      )}

      <TabContent>
        {/* Tab 1: Overview (read-only dashboard) */}
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          isConfigured={isConfigured}
          timeUnit={timeUnit}
          timeValue={timeValue}
          schedulingMode={schedulingMode}
          schedules={schedules}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
          execStatus={execStatus}
          execStatusLoading={execStatusLoading}
          refreshExecStatus={refreshExecStatus}
          onEditSchedule={() => {
            setActiveTab('2');
            setOpenSections(['schedule']);
          }}
        />

        <form noValidate style={{ minWidth: '100%' }}>
          {/* Tab 2: Configuration Wizard Accordion */}
          <TabPane tabId="2">
            {activeTab === '2' && (
              <Card bg={'light'} text={'dak'} className="mb-4" style={{ borderTopLeftRadius: 0 }}>
                <CardBody>
                  {!scheduleLoaded ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: '2rem', height: '2rem' }}
                      >
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p className="text-muted mb-0">Loading configuration...</p>
                    </div>
                  ) : (
                    <WizardAccordion
                      openSections={openSections}
                      toggleSection={toggleSection}
                      completionState={completionState}
                      sections={accordionSections}
                    />
                  )}
                </CardBody>
              </Card>
            )}
          </TabPane>

          {/* Tab 5: Risk Score (optional) */}
          {tabs.riskScore && (
            <TabPane tabId="5">
              {activeTab === '5' && (
                <Card bg={'light'} text={'dak'} className="mb-4">
                  <CardBody>
                    <RiskScore bugRisk={bugRisk} setBugRisk={setBugRisk} riskError={riskError} loading={loading} />
                  </CardBody>
                </Card>
              )}
            </TabPane>
          )}

          {/* Tab 7: Managed Products */}
          <TabPane tabId="7">
            {activeTab === '7' && (
              <ManagedProductsTab
                vendorId={vendorId}
                cachedProducts={cachedManagedProducts}
                onProductsLoaded={setCachedManagedProducts}
              />
            )}
          </TabPane>

          {/* Tab 6: History */}
          <TabPane tabId="6">{activeTab === '6' && <HistoryTab vendorId={vendorId} />}</TabPane>

          {/* Cancel and Save buttons - visible on Configuration and Risk Score tabs */}
          <Row className={activeTab === '2' || activeTab === '5' ? '' : 'd-none'}>
            <Col xs="auto" className="pl-3 pr-1">
              <Button
                color="falcon-default"
                onClick={() => {
                  if (
                    aiToggleNeedsResave &&
                    !window.confirm('AI mode was changed but priority mappings have not been saved. Leave anyway?')
                  ) {
                    return;
                  }
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto" className="pl-1">
              <SummaryModal
                isReadOnly={isReadOnly}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                isAddingCredentials={isAddingCredentials}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};

DynamicConfigForm.propTypes = {
  formSchema: PropTypes.object.isRequired,
  settings: PropTypes.object,
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string.isRequired,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  snCompanySysIdOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  snCompanySysIdErrors: PropTypes.bool,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  snCompanySysId: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setSnCompanySysId: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  bugRisk: PropTypes.array,
  setBugRisk: PropTypes.func,
  loading: PropTypes.bool,
  excludedComponents: PropTypes.array,
  setExcludedComponents: PropTypes.func,
  isServiceActive: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  allRequired: PropTypes.bool,
  setAllRequired: PropTypes.func,
  isUpdated: PropTypes.bool,
  setIsUpdated: PropTypes.func,
  autoClose: PropTypes.bool,
  setAutoClose: PropTypes.func,
  enableChatGptPriority: PropTypes.bool,
  onAiEnabledChange: PropTypes.func,
  aiEnabled: PropTypes.bool,
  aiRequired: PropTypes.bool,
  vendorAiEnabled: PropTypes.bool,
  aiToggleNeedsResave: PropTypes.bool,
  setAiToggleNeedsResave: PropTypes.func
};

export default DynamicConfigForm;
